// VIBRANIAM-SPACE v2 - useModuleGraph.js
// Graph state. The graph IS the program: the Engine reads this structure
// directly, so an edge that exists here always carries data.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NODE_TYPES, nodeDef, inputPorts, outputPorts, findPort, schemaOf, groupedNodeList,
} from '../engine/nodes/index.js';
import { topoOrder } from '../engine/evaluate.js';
import { defaultForceParams, FORCE_KINDS } from '../engine/forces.js';
import { CURVES } from '../engine/types.js';

export { NODE_TYPES, nodeDef, inputPorts, outputPorts, findPort, schemaOf, groupedNodeList };

const GRAPH_VERSION = 2;
const AUTOSAVE_KEY = 'vibraniam-space:v2:autosave';

function force(id, x, y, kind, weight, extra = {}) {
  return {
    id, type: 'r.force', x, y,
    params: { kind, weight, enabled: true, ...defaultForceParams(kind), ...extra },
  };
}

/** A patch that already shows modulation doing something on first load. */
export function defaultGraph() {
  return {
    version: GRAPH_VERSION,
    nodes: [
      { id: 'n_rms',    type: 'a.energy', x: 30,   y: 30,  params: { ...NODE_TYPES['a.energy'].params, range: 'rms', gain: 5 } },
      { id: 'n_smooth', type: 's.smooth', x: 280,  y: 30,  params: { ...NODE_TYPES['s.smooth'].params, attack: 12, release: 300 } },
      { id: 'n_onset',  type: 'a.onset',  x: 30,   y: 250, params: {} },
      { id: 'n_env',    type: 'g.env',    x: 280,  y: 250, params: { ...NODE_TYPES['g.env'].params, attack: 4, decay: 420 } },
      { id: 'n_bass',   type: 'a.energy', x: 30,   y: 470, params: { ...NODE_TYPES['a.energy'].params, range: 'bass', gain: 14 } },
      { id: 'n_lfo',    type: 'g.lfo',    x: 280,  y: 470, params: { ...NODE_TYPES['g.lfo'].params, rate: 0.05, shape: 'sine' } },

      force('f_lorenz', 560, 30,  4, 0.5),
      force('f_curl',   560, 300, 3, 0.7),
      force('f_drag',   560, 570, 2, 1.0),
      force('f_bound',  560, 740, 9, 1.0),

      { id: 'n_field',  type: 'r.field',  x: 850,  y: 40,  params: { ...NODE_TYPES['r.field'].params } },
      { id: 'n_layer',  type: 'r.layer',  x: 1150, y: 40,  params: { ...NODE_TYPES['r.layer'].params } },
      { id: 'n_camera', type: 'r.camera', x: 1150, y: 460, params: { ...NODE_TYPES['r.camera'].params } },
      { id: 'n_output', type: 'r.output', x: 1440, y: 40,  params: { ...NODE_TYPES['r.output'].params } },
    ],
    edges: [
      { id: 'e1',  from: { node: 'n_rms', port: 'out' },    to: { node: 'n_smooth', port: 'in' } },
      { id: 'e2',  from: { node: 'n_smooth', port: 'out' }, to: { node: 'n_field', port: 'p:rateAudio' }, amount: 0.55, curve: 'lin' },
      { id: 'e3',  from: { node: 'n_onset', port: 'out' },  to: { node: 'n_env', port: 'trig' } },
      { id: 'e4',  from: { node: 'n_env', port: 'out' },    to: { node: 'n_layer', port: 'p:feedbackWarp' }, amount: 0.35, curve: 'exp' },
      { id: 'e5',  from: { node: 'n_env', port: 'out' },    to: { node: 'n_camera', port: 'p:distance' }, amount: 0.06, curve: 'lin', polarity: 'neg' },
      { id: 'e6',  from: { node: 'n_bass', port: 'out' },   to: { node: 'f_curl', port: 'p:weight' }, amount: 0.30, curve: 'scurve' },
      { id: 'e7',  from: { node: 'n_lfo', port: 'out' },    to: { node: 'f_lorenz', port: 'p:weight' }, amount: 0.18, curve: 'lin' },

      { id: 'e8',  from: { node: 'f_lorenz', port: 'out' }, to: { node: 'n_field', port: 'forces' } },
      { id: 'e9',  from: { node: 'f_curl', port: 'out' },   to: { node: 'n_field', port: 'forces' } },
      { id: 'e10', from: { node: 'f_drag', port: 'out' },   to: { node: 'n_field', port: 'forces' } },
      { id: 'e11', from: { node: 'f_bound', port: 'out' },  to: { node: 'n_field', port: 'forces' } },

      { id: 'e12', from: { node: 'n_field', port: 'out' },  to: { node: 'n_layer', port: 'in' } },
      { id: 'e13', from: { node: 'n_layer', port: 'out' },  to: { node: 'n_output', port: 'in' } },
      { id: 'e14', from: { node: 'n_camera', port: 'out' }, to: { node: 'n_output', port: 'camera' } },
    ],
  };
}

function createsCycle(edges, from, to) {
  const adjacency = new Map();
  for (const e of [...edges, { from, to }]) {
    if (!adjacency.has(e.from.node)) adjacency.set(e.from.node, []);
    adjacency.get(e.from.node).push(e.to.node);
  }
  const seen = new Set();
  const stack = new Set();
  const visit = (n) => {
    if (stack.has(n)) return true;
    if (seen.has(n)) return false;
    seen.add(n); stack.add(n);
    for (const next of adjacency.get(n) || []) if (visit(next)) return true;
    stack.delete(n);
    return false;
  };
  return [...adjacency.keys()].some(visit);
}

function clampField(raw, field, fallback) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  if (field.min !== undefined && n < field.min) return field.min;
  if (field.max !== undefined && n > field.max) return field.max;
  return n;
}

/**
 * Rebuilds a node's params against its schema: unknown keys from a foreign or
 * hand-edited file are dropped, numeric fields are clamped to their declared
 * range, and a bad/missing r.force `kind` falls back to the type's default
 * before its dynamic (kind-dependent) fields are resolved. Used by both
 * import and the autosave restore path, so a corrupted value can never reach
 * the engine (and from there the WASM side, which aborts hard on bad input).
 */
export function sanitizeParams(type, rawParams) {
  const def = NODE_TYPES[type];
  if (!def) return {};
  let params = { ...def.params, ...(rawParams && typeof rawParams === 'object' ? rawParams : {}) };

  if (type === 'r.force') {
    const kind = Number(params.kind);
    const valid = FORCE_KINDS.some((f) => f.kind === kind);
    params = { ...params, kind: valid ? kind : def.params.kind };
    params = { ...defaultForceParams(params.kind), ...params, kind: params.kind };
  }

  for (const f of schemaOf({ type, params })) {
    if (f.min === undefined && f.max === undefined) continue;
    params[f.key] = clampField(params[f.key], f, def.params[f.key] ?? f.min ?? 0);
  }
  return params;
}

/**
 * Parses and validates a serialized graph (from a file import or the
 * localStorage autosave). Throws with a human-readable message on anything
 * it cannot make sense of; never returns a graph that can crash the engine.
 */
export function parseGraphPayload(json) {
  let parsed;
  if (typeof json === 'string') {
    try {
      parsed = JSON.parse(json);
    } catch {
      throw new Error('Il file non è un JSON valido (sintassi non corretta o file vuoto).');
    }
  } else {
    parsed = json;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Formato patch non valido: atteso un oggetto JSON.');
  }
  if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
    throw new Error('Formato patch non valido: mancano "nodes" o "edges".');
  }

  const nodes = parsed.nodes
    .filter((n) => n && typeof n === 'object' && n.id !== undefined && n.id !== null && NODE_TYPES[n.type])
    .map((n) => ({
      id: String(n.id),
      type: n.type,
      x: Number.isFinite(Number(n.x)) ? Number(n.x) : 0,
      y: Number.isFinite(Number(n.y)) ? Number(n.y) : 0,
      params: sanitizeParams(n.type, n.params),
    }));

  if (parsed.nodes.length > 0 && nodes.length === 0) {
    throw new Error('Nessun modulo riconosciuto: file di una versione incompatibile?');
  }

  // De-duplicate ids a hand-edited file might repeat - keep the first.
  const seen = new Set();
  const dedupedNodes = nodes.filter((n) => (seen.has(n.id) ? false : (seen.add(n.id), true)));
  const ids = new Set(dedupedNodes.map((n) => n.id));

  const edges = parsed.edges
    .filter((e) => e && typeof e === 'object'
      && e.from?.node && e.from?.port && e.to?.node && e.to?.port
      && ids.has(e.from.node) && ids.has(e.to.node))
    .map((e, i) => {
      const edge = {
        id: e.id !== undefined && e.id !== null ? String(e.id) : `e_${i}`,
        from: { node: String(e.from.node), port: String(e.from.port) },
        to: { node: String(e.to.node), port: String(e.to.port) },
      };
      const amount = Number(e.amount);
      if (Number.isFinite(amount)) edge.amount = Math.max(-1, Math.min(1, amount));
      if (CURVES.includes(e.curve)) edge.curve = e.curve;
      if (e.polarity === 'neg' || e.polarity === 'pos') edge.polarity = e.polarity;
      return edge;
    });

  // De-duplicate edge ids too, for the same hand-edited-file reason.
  const seenEdges = new Set();
  const dedupedEdges = edges.filter((e) => (seenEdges.has(e.id) ? false : (seenEdges.add(e.id), true)));

  return { version: GRAPH_VERSION, nodes: dedupedNodes, edges: dedupedEdges };
}

function loadAutosave() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTOSAVE_KEY) : null;
    if (!raw) return null;
    return parseGraphPayload(raw);
  } catch {
    return null;
  }
}

export function useModuleGraph(initial) {
  const [graph, setGraph] = useState(() => initial || loadAutosave() || defaultGraph());
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [lastError, setLastError] = useState(null);
  // Fotosensitivity: one-time-per-session confirmation before any strobe node
  // (Image Strobe or Mixer/Switcher) can actually flash.
  const [strobePrompt, setStrobePrompt] = useState(null); // node id awaiting confirmation
  const strobeConfirmedRef = useRef(false);
  const counter = useRef(2000);
  const nextId = (prefix) => `${prefix}_${++counter.current}`;

  const addNode = useCallback((type, x = 60, y = 60) => {
    const def = NODE_TYPES[type];
    if (!def) return null;
    const node = { id: nextId('n'), type, x: Math.round(x), y: Math.round(y), params: { ...def.params } };
    setGraph((g) => ({ ...g, nodes: [...g.nodes, node] }));
    setSelectedId(node.id);
    if ((type === 'r.imageStrobe' || type === 'r.mixer') && !strobeConfirmedRef.current) {
      setStrobePrompt(node.id);
    }
    return node.id;
  }, []);

  const removeNode = useCallback((id) => {
    setGraph((g) => ({
      ...g,
      nodes: g.nodes.filter((n) => n.id !== id),
      edges: g.edges.filter((e) => e.from.node !== id && e.to.node !== id),
    }));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const duplicateNode = useCallback((id) => {
    setGraph((g) => {
      const src = g.nodes.find((n) => n.id === id);
      if (!src) return g;
      const copy = { ...src, id: nextId('n'), x: src.x + 28, y: src.y + 28, params: { ...src.params } };
      return { ...g, nodes: [...g.nodes, copy] };
    });
  }, []);

  const moveNode = useCallback((id, x, y) => {
    setGraph((g) => ({ ...g, nodes: g.nodes.map((n) => (n.id === id ? { ...n, x, y } : n)) }));
  }, []);

  const setParam = useCallback((id, key, value) => {
    setGraph((g) => ({
      ...g,
      nodes: g.nodes.map((n) => {
        if (n.id !== id) return n;
        const def = NODE_TYPES[n.type];
        if (def?.onParamChange) {
          const replacement = def.onParamChange(key, value, n.params);
          if (replacement) return { ...n, params: replacement };
        }
        return { ...n, params: { ...n.params, [key]: value } };
      }),
    }));
  }, []);

  const connect = useCallback((from, to) => {
    let ok = false;
    setGraph((g) => {
      const fromNode = g.nodes.find((n) => n.id === from.node);
      const toNode = g.nodes.find((n) => n.id === to.node);
      if (!fromNode || !toNode) { setLastError('Nodo non trovato'); return g; }
      if (from.node === to.node) { setLastError('Un nodo non puo collegarsi a se stesso'); return g; }

      const outPort = findPort(fromNode, from.port, 'out');
      const inPort = findPort(toNode, to.port, 'in');
      if (!outPort || !inPort) { setLastError('Porta non valida'); return g; }

      // Parameter ports accept anything that reduces to a number.
      const isParam = to.port.startsWith('p:');
      const compatible = isParam
        ? ['scalar', 'trigger', 'array'].includes(outPort.kind)
        : outPort.kind === inPort.kind;
      if (!compatible) {
        setLastError(`Tipi incompatibili: ${outPort.kind} -> ${inPort.kind}`);
        return g;
      }

      const already = g.edges.some((e) => e.to.node === to.node && e.to.port === to.port
                                        && e.from.node === from.node && e.from.port === from.port);
      if (already) { setLastError('Collegamento gia presente'); return g; }

      // Declared single inputs hold one cable; parameter ports and multi ports
      // accept several, because modulation slots stack.
      const multi = inPort.multi || isParam;
      let edges = g.edges;
      if (!multi) edges = edges.filter((e) => !(e.to.node === to.node && e.to.port === to.port));

      if (createsCycle(edges, from, to)) {
        setLastError('Questo collegamento creerebbe un ciclo');
        return g;
      }

      ok = true;
      setLastError(null);
      const edge = { id: nextId('e'), from, to };
      if (isParam) { edge.amount = 0.25; edge.curve = 'lin'; edge.polarity = 'pos'; }
      return { ...g, edges: [...edges, edge] };
    });
    return ok;
  }, []);

  const disconnect = useCallback((edgeId) => {
    setGraph((g) => ({ ...g, edges: g.edges.filter((e) => e.id !== edgeId) }));
    setSelectedEdgeId((cur) => (cur === edgeId ? null : cur));
  }, []);

  const setEdgeConfig = useCallback((edgeId, patch) => {
    setGraph((g) => ({
      ...g,
      edges: g.edges.map((e) => (e.id === edgeId ? { ...e, ...patch } : e)),
    }));
  }, []);

  const reset = useCallback(() => {
    setGraph(defaultGraph());
    setSelectedId(null);
    setSelectedEdgeId(null);
    setLastError(null);
  }, []);

  const confirmStrobe = useCallback((nodeId) => {
    strobeConfirmedRef.current = true;
    setStrobePrompt(null);
    if (nodeId) setParam(nodeId, 'confirmed', true);
  }, [setParam]);

  const dismissStrobe = useCallback(() => {
    setStrobePrompt(null);
    // Node stays added but frozen (confirmed stays false) until the user
    // confirms via the node's own "Conferma avviso" toggle.
  }, []);

  const serialize = useCallback(
    () => JSON.stringify({ ...graph, version: GRAPH_VERSION }, null, 2), [graph]
  );

  const load = useCallback((json) => {
    try {
      const next = parseGraphPayload(json);
      setGraph(next);
      setSelectedId(null);
      setSelectedEdgeId(null);
      setLastError(null);
      return true;
    } catch (err) {
      setLastError(err.message || 'Patch non valida');
      return false;
    }
  }, []);

  // Debounced autosave, so a crash or accidental reload never loses more
  // than a fraction of a second of work. Position updates during a drag fire
  // this often, hence the debounce instead of writing on every change.
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ ...graph, version: GRAPH_VERSION }));
      } catch {
        /* storage full or unavailable (private browsing) - non-fatal */
      }
    }, 400);
    return () => clearTimeout(saveTimerRef.current);
  }, [graph]);

  // topoOrder depends only on node ids and edge connectivity, not on x/y
  // positions. Recompute it only when that structure changes, so a drag (which
  // updates x/y on every move) does not re-run the topological sort.
  const structureKey = useMemo(() => {
    const validNodes = graph.nodes.filter(n => n && typeof n === 'object' && n.id !== undefined && n.id !== null);
    const nodeIds = validNodes.map(n => n.id).sort().join(',');
    const validEdges = graph.edges.filter(e => e && typeof e === 'object' && e.from && e.from.node && e.to && e.to.node);
    const edgesKey = validEdges.map(e => `${e.from.node}:${e.from.port}:${e.to.node}:${e.to.port}`).sort().join(',');
    return `${nodeIds}|${edgesKey}`;
  }, [graph.nodes, graph.edges]);

  const order = useMemo(() => topoOrder(graph.nodes, graph.edges), [structureKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const issues = useMemo(() => {
    const list = [];
    if (!order) list.push('Il grafo contiene un ciclo');
    const has = (t) => graph.nodes.some((n) => n.type === t);
    if (!has('r.field')) list.push('Nessun substrato: aggiungi un Campo particellare');
    if (!has('r.output')) list.push('Nessuna Uscita: le impostazioni di export usano i valori di default');
    for (const n of graph.nodes) {
      if (n.type !== 'r.field') continue;
      const forces = graph.edges.filter((e) => e.to.node === n.id && e.to.port === 'forces');
      if (!forces.length) list.push('Campo particellare senza forze collegate');
    }
    return list;
  }, [graph, order]);

  const modCount = useMemo(
    () => graph.edges.filter((e) => e.to.port.startsWith('p:')).length, [graph]
  );

  return {
    graph, nodes: graph.nodes, edges: graph.edges,
    order, issues, modCount,
    selectedId, setSelectedId,
    selectedEdgeId, setSelectedEdgeId,
    lastError, setLastError,
    addNode, removeNode, duplicateNode, moveNode, setParam,
    connect, disconnect, setEdgeConfig,
    reset, serialize, load,
    strobePrompt, confirmStrobe, dismissStrobe,
  };
}

export default useModuleGraph;
